import type { State } from '@staiter/shared/task-module';

/**
 * WTP-F7 (решение владельца В-13, 28.08.2026) — дисциплина ОТТЕНКОВ внутри группы.
 *
 * ЧТО БЫЛО. Иконка и цвет статуса задаются ГРУППОЙ, а групп ровно шесть. «В
 * работе» и «На проверке» — два пользовательских статуса внутри одной группы
 * `started`, и на стенде им выдан один и тот же `color_token`: один глиф, один
 * цвет, различает только подпись. `STAITER_UI §1.3` требует три канала —
 * цвет, форма и текст, — а работали два.
 *
 * ЧТО РЕШИЛ ВЛАДЕЛЕЦ. Не править модель (поле иконки у статуса отложено как
 * улучшение по запросу пользователей), а завести заранее заданную палитру
 * оттенков: статусы внутри группы получают РАЗНЫЕ оттенки автоматически, по
 * порядку. Модель не трогается — оттенок вычисляется из того, что уже есть.
 *
 * КАК СЧИТАЕТСЯ. Оттенок нужен только там, где есть КОЛЛИЗИЯ: два статуса
 * одной группы с одинаковым токеном цвета. Первый из них (по `sequence` —
 * порядку колонок доски) остаётся базовым, второй и третий получают ступени
 * подмеса. Статус с уникальным токеном не сдвигается вовсе: правило лечит
 * неразличимость, а не перекрашивает продукт.
 *
 * ПОЧЕМУ ПОДМЕС `--text-primary`, А НЕ ФИКСИРОВАННЫЕ ЦВЕТА. Он разворачивается
 * по темам сам: в светлой теме `--text-primary` тёмный (оттенок уходит вниз по
 * светлоте), в тёмной — светлый (вверх). Пара фиксированных цветов потребовала
 * бы своей ветки на каждую тему, и одна из них неизбежно отстала бы.
 */

/**
 * Ступени подмеса, %. Ноль — базовый токен группы (без сдвига).
 *
 * Раунд 1 гейта WTP (UI, MAJOR): «внутригрупповой оттенок неразличим: ΔL 0.054
 * приходится на текст чипа, а на заливке 12–15% alpha остаётся ~0.008 L …
 * тратить ступень на заливку/рамку чипа шагом ≥0.09 либо различать формой».
 * Ступень поднята до ≥0.09 по светлоте, а форма добавлена вторым каналом
 * отдельно (см. `stateShadeFilled`): один канал различения — это тот же дефект,
 * от которого уходили, только другой стороной.
 *
 * Величина считается от НАСТОЯЩЕГО основания. Первая редакция брала 26% и давала
 * ΔL ≈ 0.088 — но от базы 0.58, то есть от СВЕТЛОГО значения палитры, которое в
 * тёмной теме применялось по ошибке (разбор — в `tokens.css`, блок
 * `:root[data-theme="dark"]`). После починки основание стало 0.70, потолок
 * `--text-primary` — 0.92, и та же доля дала бы уже ΔL 0.057. 40% дают ΔL ≈ 0.088
 * при верном основании; 65% — ≈ 0.143.
 */
export const STATE_SHADE_STEPS = [0, 40, 65] as const;

type ShadeInput = Pick<State, 'id' | 'color_token' | 'group' | 'sequence'>;

/**
 * Ступень оттенка статуса.
 *
 * РЕШЕНИЕ ВЛАДЕЛЬЦА 31.08.2026, дословно: «Закрепляем». Оттенок стал ПОЛЕМ
 * статуса (`shade_index`, migration 0120) и больше не зависит от позиции:
 * ревьюер назвал цену прежнего расчёта на раунде 1 гейта WTP — «вставка статуса
 * в настройках проекта сдвигает индексы и перекрашивает соседей — выученный цвет
 * перестаёт значить то же самое».
 *
 * Порядковый расчёт остаётся ФОЛБЭКОМ и только им: строка без поля приходит от
 * старого сервера либо из места, которое статус ещё не досылает, и тогда лучше
 * посчитать по-старому, чем показать всем базовый цвет и вернуть неразличимость.
 * После того как читателей без поля не останется (грепом, а не по памяти),
 * фолбэк снимается — он мёртвый код, и называть его надо мёртвым.
 */
export function stateShadeIndex(
  states: readonly ShadeInput[] | undefined,
  state: Pick<State, 'id' | 'color_token' | 'group'> & { shade_index?: number },
): number {
  if (typeof state.shade_index === 'number') return state.shade_index;
  if (!states || states.length === 0) return 0;
  const siblings = states
    .filter((s) => s.group === state.group && s.color_token === state.color_token)
    .sort((a, b) => a.sequence - b.sequence);
  if (siblings.length < 2) return 0;
  const idx = siblings.findIndex((s) => s.id === state.id);
  if (idx <= 0) return 0;
  // Больше трёх одинаковых токенов в группе — редкость; цикл по палитре честнее
  // «всё, что дальше, одинаковое»: хотя бы соседи по списку остаются разными.
  return idx % STATE_SHADE_STEPS.length;
}

/** CSS-значение цвета статуса с учётом оттенка (0 → базовый токен). */
export function stateColorValue(colorToken: string, shadeIndex = 0): string {
  const base = `var(--column-color-${colorToken})`;
  const step = STATE_SHADE_STEPS[shadeIndex % STATE_SHADE_STEPS.length] ?? 0;
  if (step === 0) return base;
  return `color-mix(in oklch, ${base}, var(--text-primary) ${step}%)`;
}

/**
 * Второй канал различения — ФОРМА глифа, а не только светлота.
 *
 * `STAITER_UI §1.3` требует, чтобы цвет никогда не был единственным носителем
 * различия, и это же требование спасает случай, который поймал гейт: на
 * маленьком глифе разница в светлоте читается плохо, а заливка круга видна
 * сразу и переживает и дальтонизм, и плохой монитор. Базовый статус группы
 * остаётся контурным, следующие — залитыми.
 */
export function stateShadeFilled(shadeIndex: number): boolean {
  return shadeIndex % STATE_SHADE_STEPS.length !== 0;
}
